import { DecimalPipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ChartModule } from 'primeng/chart';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-energy-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './energy-chart.component.html',
  styleUrl: './energy-chart.component.css'
})
export class EnergyChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;
  labels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  gasActualLabel: string = "Gas Actual";
  electricityActualLabel: string = "Electricity Actual";
  gasForecastLabel: string = "Gas Forecast";
  electricityForecastLabel: string = "Electricity Forecast";
  smcLabel: string = "smc";
  kwhLabel: string = "kWh";

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummygasActualData: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  dummyelectricityActualData: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  dummygasForecastData: number[] = [0, 0, 0, 0, 0, 0, 0, 130, 120, 135, 140, 120];
  dummyelectricityForecastData: number[] = [0, 0, 0, 0, 0, 0, 0, 130, 120, 135, 140, 120];

  dummygasActualConsumption: number[] = [200, 166.67, 216.67, 183.33, 191.67, 200, 208.33, 0, 0, 0, 0, 0];
  dummygasForecastConsumption: number[] = [200, 166.67, 216.67, 183.33, 191.67, 200, 208.33, 0, 0, 0, 0, 0];
  dummyelectricityActualConsumption: number[] = [400, 333.33, 433.33, 366.67, 383.33, 400, 416.67, 0, 0, 0, 0, 0];
  dummyelectricityForecastConsumption: number[] = [400, 333.33, 433.33, 366.67, 383.33, 400, 416.67, 0, 0, 0, 0, 0];

  @Input() gasActualData: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  @Input() electricityActualData: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  @Input() gasForecastData: number[] = [0, 0, 0, 0, 0, 0, 0, 130, 120, 135, 140, 120];
  @Input() electricityForecastData: number[] = [0, 0, 0, 0, 0, 0, 0, 130, 120, 135, 140, 120];

  @Input() gasActualConsumption: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  @Input() gasForecastConsumption: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  @Input() electricityActualConsumption: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];
  @Input() electricityForecastConsumption: number[] = [120, 100, 130, 110, 115, 120, 125, 0, 0, 0, 0, 0];

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.gas_actual'),
      this.translocoService.selectTranslate('dashboard.electricity_actual'),
      this.translocoService.selectTranslate('dashboard.gas_forecast'),
      this.translocoService.selectTranslate('dashboard.electricity_forecast'),
      this.translocoService.selectTranslate('shared.smc'),
      this.translocoService.selectTranslate('shared.kwh')
    ]).subscribe((
      [
        gasActualTranslation,
        electricityActualTranslation,
        gasForecastTranslation,
        electricityForecastTranslation,
        smcTranslation,
        kwhTranslation
      ]
    ) => {
      this.gasActualLabel = gasActualTranslation;
      this.electricityActualLabel = electricityActualTranslation;
      this.gasForecastLabel = gasForecastTranslation;
      this.electricityForecastLabel = electricityForecastTranslation;
      this.smcLabel = smcTranslation;
      this.kwhLabel = kwhTranslation;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.gasActualData = this.dummygasActualData;
      this.electricityActualData = this.dummyelectricityActualData;
      this.gasForecastData = this.dummygasForecastData;
      this.electricityForecastData = this.dummyelectricityForecastData;

      this.gasActualConsumption = this.dummygasActualConsumption;
      this.gasForecastConsumption = this.dummygasForecastConsumption;
      this.electricityActualConsumption = this.dummyelectricityActualConsumption;
      this.dummyelectricityForecastConsumption = this.dummyelectricityForecastConsumption;
    }

    this.data = {
      labels: this.labels,
      datasets: [
        {
          label: this.gasActualLabel,
          type: 'bar',
          backgroundColor: '#80A7A5',
          data: this.gasActualConsumption,
          borderRadius: 10,
        },
        {
          label: this.electricityActualLabel,
          type: 'bar',
          backgroundColor: '#A9BE92',
          data: this.electricityActualConsumption,
          borderRadius: 10,
        },
        {
          label: this.gasForecastLabel,
          type: 'bar',
          backgroundColor: '#C0D3D2',
          data: this.gasForecastConsumption,
          borderRadius: 10,
        },
        {
          label: this.electricityForecastLabel,
          type: 'bar',
          backgroundColor: '#C9D6BB',
          data: this.electricityForecastConsumption,
          borderRadius: 10,
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            color: '#000000',
            font: {
              size: 12,
            },
          },
        },
        datalabels: {
          display: (context: any) => {
            const value = context.dataset.data[context.dataIndex];
            return value !== null && value !== 0 ? 'auto' : false;
          },
          clamp: true,
          clip: true,
          align: 'center',
          anchor: 'center',
          formatter: (value: number, context: any) => {
            const isGas = context.datasetIndex === 0 || context.datasetIndex === 2;
            const unit = isGas ? this.smcLabel : this.kwhLabel;
            return (this.decimalPipe.transform(value, '1.0-0') || value) + ' ' + unit;
          },
          font: {
            size: 10,
            weight: 'normal',
            family: 'Gilroy',
          },
          color: '#000000'
        },
        tooltip: {
          callbacks: {
            label: this.formatTooltipLabel.bind(this)
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          display: true,
          grid: {
            display: false
          },
          ticks: {
            display: true
          }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: {
            display: true,
          },
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
      },
    };
  }

  formatTooltipLabel(tooltipItem: any) {
    let label = tooltipItem.dataset.label || '';
    let consumptionValue = tooltipItem.raw || 0;

    const index = tooltipItem.datasetIndex;
    const isGas = index === 0 || index === 2;
    const unit = isGas ? this.smcLabel : this.kwhLabel;

    let costArray: number[] = [];
    const dataIndex = tooltipItem.dataIndex;

    switch (index) {
      case 0:
        costArray = this.gasActualData;
        break;
      case 1:
        costArray = this.electricityActualData;
        break;
      case 2:
        costArray = this.gasForecastData;
        break;
      case 3:
        costArray = this.electricityForecastData;
        break;
    }

    const costValue = (costArray[dataIndex] || 0);
    const cost = `${this.decimalPipe.transform(costValue, '1.0-0') || costValue}€`;
    const consumption = `${this.decimalPipe.transform(consumptionValue, '1.0-0') || consumptionValue} ${unit}`;

    return [`${label}`, consumption, cost];
  }
}
